import { CHECKLIST_STEPS } from '../../data';
import { PRIORITY_LABEL } from '../../constants';
import { isDone } from '../../utils';
import s from '../../styles/dashboard.module.css';

function CheckStep({ label, checked, onToggle }) {
  return (
    <button type="button" className={`${s.checkStep} ${checked ? s.done : ''}`} onClick={onToggle}>
      <span className={s.checkDot}>{checked && '✓'}</span>
      {label}
    </button>
  );
}

function MiniAvatar({ member, color, onRemove }) {
  const name = member.name || `${member.given_name || ''} ${member.family_name || ''}`.trim();
  const initials = member.initials || name.split(' ').filter(Boolean).map(n => n[0]).join('').toUpperCase();
  return (
    <div style={{
      width: 26, height: 26, borderRadius: '50%', background: `${color}22`,
      border: `1.5px solid ${color}66`, display: 'flex', alignItems: 'center',
      justifyContent: 'center', fontSize: 9, fontWeight: 600, color,
      cursor: 'pointer', flexShrink: 0,
    }} onClick={onRemove} title={`${name} — click to unassign`}>
      {initials}
    </div>
  );
}

export function AppointmentCard({ appt, date, onToggleCheck, onTogglePriority, accentColor, staff, onAssignStaff, onUnassignStaff, dragOverId, setDragOverId, onUploadClick, onViewPhotos, onNextStep, uploadStatus }) {
  const done     = isDone(appt);
  const services = appt.services?.length > 0 ? appt.services : [appt.service];
  const cardId   = `${appt.location}-${appt.id}`;
  const isOver   = dragOverId === cardId;
  const assigned = appt.assignedStaff || [];
  const priority = appt.priority || 'medium';
  const isUrgent = priority === 'urgent';
  const urgentBg = isUrgent ? 'rgba(160,30,30,0.13)' : 'transparent';
  const urgentColor = isUrgent ? '#e03030' : '#ff9999';

  return (
    <div
      className={`${s.apptCard} ${done ? s.done : ''}`}
      style={{
        borderLeftColor: done ? '#2a2520' : isUrgent ? '#e03030' : accentColor,
        borderLeftWidth: isUrgent ? 4 : 3,
        background: done ? undefined : urgentBg,
        outline: isOver ? `2px solid ${accentColor}` : 'none',
      }}
      onDragOver={e => { e.preventDefault(); setDragOverId(cardId); }}
      onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOverId(null); }}
      onDrop={e => {
        e.preventDefault(); setDragOverId(null);
        const memberId = e.dataTransfer.getData('memberId');
        if (memberId) onAssignStaff(appt.location, appt.id, memberId);
      }}
    >
      {isUrgent && !done && (
        <div className={s.urgentBanner}>
          <span className={s.urgentLabel}>⚠ URGENT</span>
          <button onClick={() => onTogglePriority(appt.location, appt.id)} className={s.urgentChangeBtn}>Change</button>
        </div>
      )}

      <div className={s.apptHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span className={s.apptTime} style={{ color: isUrgent ? '#ff8888' : undefined }}>{appt.time}</span>
          {/* {!isUrgent && (
            <button onClick={() => onTogglePriority(appt.location, appt.id)}
                    style={{ cursor: 'pointer', fontFamily: 'inherit', fontSize: 8, letterSpacing: 1.5, textTransform: 'uppercase', padding: '3px 8px', borderRadius: 4, fontWeight: 600, border: 'none',
                      ...(priority === 'high'   ? { background: 'rgba(201,149,106,0.13)', color: '#c9956a', outline: '1px solid rgba(201,149,106,0.27)' } :
                          priority === 'medium' ? { background: 'rgba(74,138,110,0.13)',  color: '#6db88a', outline: '1px solid rgba(74,138,110,0.27)'  } :
                                                  { background: 'rgba(90,80,72,0.2)',     color: '#7a6e62', outline: '1px solid rgba(90,80,72,0.3)'     }),
                    }}>
              {PRIORITY_LABEL[priority]}
            </button>
          )} */}
          {appt.totalAmount && <span className={s.cardAmountTag} style={{ color: isUrgent ? urgentColor : undefined }}>{appt.totalAmount}</span>}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Gallery button — shown only when Photos step is checked */}
          {appt.checks[1] && (
            <button
              onClick={() => onViewPhotos(appt)}
              title="View photos"
              className={s.galleryBtn}
            >🖼</button>
          )}
          {/* Camera button */}
          <button
            onClick={() => onUploadClick(appt)}
            title="Upload photos"
            disabled={uploadStatus === 'uploading'}
            className={`${s.cameraBtn} ${uploadStatus === 'done' ? s.cameraBtnDone : uploadStatus === 'error' ? s.cameraBtnError : ''}`}
          >
            {uploadStatus === 'uploading' ? '⏳' : uploadStatus === 'done' ? '✓' : uploadStatus === 'error' ? '✗' : '📷'}
          </button>
          <span className={s.apptDue} style={{ color: isUrgent ? urgentColor : undefined }}>
            {appt.bay ? `BAY ${appt.bay}` : 'QUEUE'}
          </span>
        </div>
      </div>

      <div className={s.apptInfo}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className={`${s.apptCar} ${done ? s.crossed : ''}`}
               style={{ fontSize: 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: isUrgent && !done ? urgentColor : undefined }}>
            {appt.customer}
          </div>
        </div>
        <span className={s.apptDetailer} style={{ fontSize: 9 }}>{appt.detailer}</span>
      </div>

      <div className={s.serviceList}>
        {services.map((svc, i) => (
          <div key={i} className={s.serviceRow} style={{ marginBottom: i < services.length - 1 ? 3 : 0 }}>
            <span className={s.serviceBullet} style={{ color: isUrgent ? urgentColor : accentColor }}>▸</span>
            <span className={`${s.serviceText} ${done ? s.serviceTextDone : ''}`} style={{ color: isUrgent && !done ? urgentColor : undefined }}>
              {svc}
            </span>
          </div>
        ))}
      </div>

      {appt.bookedBy && (
        <div className={`${s.bookedInfo} ${isUrgent && !done ? s.bookedInfoUrgent : ''}`}>
          Booked by {appt.bookedBy}
          {appt.bookedAtLabel ? ` · ${appt.bookedAtLabel}` : ''}
        </div>
      )}

      {assigned.length > 0 && (
        <div className={s.assignedRow}>
          <span className={s.assignedLabel}>On this car:</span>
          {assigned.map(memberId => {
            const m = staff.find(x => x.squareId === memberId);
            if (!m) return null;
            return <MiniAvatar key={memberId} member={m} color={isUrgent ? urgentColor : accentColor}
                               onRemove={() => onUnassignStaff(appt.location, appt.id, memberId)} />;
          })}
          <span className={s.assignedHint}>click to remove</span>
        </div>
      )}

      {isOver && (
        <div className={s.dropTarget} style={{ border: `1px dashed ${isUrgent ? urgentColor : accentColor}`, color: isUrgent ? urgentColor : accentColor }}>
          DROP TO ASSIGN
        </div>
      )}

      {appt.notes && (
        <div className={s.notesBox} style={{ borderLeft: `2px solid ${isUrgent ? urgentColor + '55' : accentColor + '55'}` }}>
          <div className={s.notesLabel}>Notes</div>
          <div className={s.notesText}>{appt.notes}</div>
        </div>
      )}

      {appt.customerNote && (
        <div className={s.notesBox} style={{ borderLeft: `2px solid ${isUrgent ? urgentColor + '55' : accentColor + '55'}` }}>
          <div className={s.notesLabel}>Customer Note</div>
          <div className={s.notesText}>{appt.customerNote}</div>
        </div>
      )}

      {/* TODO: disable for now */}
      {/* <div className={s.controllerRow}>
        <button onClick={() => onNextStep(appt)} className={s.nextStepBtn} disabled={done}>Next</button>
      </div> */}

      <div className={s.checklistRow} style={{ gap: 4 }}>
        {CHECKLIST_STEPS.map((step, i) => (
          <CheckStep
            key={step}
            label={step}
            checked={appt.checks[i]}
            onToggle={() => onToggleCheck(appt.location, appt.id, i, step)}
          />
        ))}
      </div>
    </div>
  );
}
